import { useState } from 'react';
import { useParams, useNavigate } from 'react-router';
import { ArrowLeft, CheckCircle, Eye, X, Printer } from 'lucide-react';
import { toast } from 'sonner';
import { useSelector } from 'react-redux';
import { selectCompany } from '@/store/slices/companySlice';
import { PageHeader } from '@/components/shared/PageHeader';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { Avatar } from '@/components/shared/AvatarWithInitials';
import { Button } from '@/components/ui/button';
import { formatCurrency } from '@/lib/formatters';
import { usePayrollRun, useMarkPayrollPaid, usePayslip } from '@/api/queries/payroll.queries';

// ─── Payslip Modal ────────────────────────────────────────────────────────────

function PayslipModal({ runId, item, onClose }) {
  const { data } = usePayslip(runId, item.id);
  const ps = data?.data || item;
  const company = useSelector(selectCompany);
  const payPeriod = ps.pay_period || item.pay_period;

  const trow = (l, v) => <tr className="border-b border-gray-100"><td className="py-1.5 pr-4 text-[12px] text-gray-500">{l}</td><td className="py-1.5 text-[12px] font-semibold text-gray-900">{v || '—'}</td></tr>;
  const erow = (l, v, bold) => <tr className="border-b border-gray-100"><td className={`py-1.5 pr-4 text-[12px] ${bold?'font-bold text-gray-900':'text-gray-600'}`}>{l}</td><td className={`py-1.5 text-right text-[12px] ${bold?'font-bold text-gray-900':'text-gray-800'}`}>{formatCurrency(v)}</td></tr>;
  const drow = (l, v, bold) => <tr className="border-b border-gray-100"><td className={`py-1.5 pr-4 text-[12px] ${bold?'font-bold text-gray-900':'text-gray-600'}`}>{l}</td><td className={`py-1.5 text-right text-[12px] ${bold?'font-bold text-red-600':'text-red-500'}`}>−{formatCurrency(v)}</td></tr>;
  const totalDed = Number(ps.pf_employee)+Number(ps.esic_employee)+Number(ps.pt)+Number(ps.tds)+Number(ps.other_deductions);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-white rounded-[16px] shadow-2xl w-full max-w-2xl max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 bg-gray-50 rounded-t-[16px] shrink-0">
          <p className="text-[13px] font-bold text-gray-900">Payslip — {payPeriod}</p>
          <div className="flex items-center gap-2">
            <button onClick={() => window.print()} className="flex items-center gap-1.5 px-3 py-1.5 rounded-[8px] border border-gray-200 text-[12px] font-semibold text-gray-600 hover:bg-gray-100">
              <Printer size={12}/> Print
            </button>
            <button onClick={onClose} className="p-1.5 rounded-[8px] hover:bg-gray-100"><X size={16}/></button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-5 font-sans text-gray-900">
          {/* Header */}
          <div className="flex items-start justify-between pb-4 border-b-2 border-gray-900">
            <div>
              <p className="text-[20px] font-black">{company?.name || 'Company'}</p>
              {ps.company_pf_number && <p className="text-[11px] text-gray-500">PF Reg: {ps.company_pf_number}</p>}
              {ps.company_esic_number && <p className="text-[11px] text-gray-500">ESIC: {ps.company_esic_number}</p>}
            </div>
            <div className="text-right">
              <p className="text-[11px] font-bold text-gray-400 uppercase tracking-widest">Salary Slip</p>
              <p className="text-[14px] font-bold">{payPeriod}</p>
              <p className="text-[11px] text-gray-500">{ps.period_start} to {ps.period_end}</p>
            </div>
          </div>

          {/* Employee + Payment */}
          <div className="grid grid-cols-2 gap-6">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-2">Employee</p>
              <table className="w-full"><tbody>
                {trow('Name', ps.employee_name)}
                {trow('Code', ps.employee_code)}
                {trow('Department', ps.department)}
                {trow('Designation', ps.designation)}
                {trow('Type', ps.employment_type?.replace('_',' '))}
              </tbody></table>
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-2">Payment Info</p>
              <table className="w-full"><tbody>
                {trow('UAN', ps.uan)}
                {trow('PAN', ps.pan)}
                {trow('Bank', ps.bank_name)}
                {trow('Account', ps.account_number ? `****${String(ps.account_number).slice(-4)}` : null)}
                {trow('Mode', ps.payment_mode?.replace('_',' '))}
              </tbody></table>
            </div>
          </div>

          {/* Attendance */}
          <div className="grid grid-cols-4 gap-3">
            {[['Working Days', ps.total_working_days],['Present', ps.present_days],['Paid Leave', ps.paid_leave_days],['LOP', ps.lop_days]].map(([l,v])=>(
              <div key={l} className="p-3 bg-gray-50 rounded-[8px] text-center border border-gray-100">
                <p className="text-[10px] text-gray-400 uppercase">{l}</p>
                <p className="text-[20px] font-black">{v}</p>
              </div>
            ))}
          </div>

          {/* Earnings & Deductions */}
          <div className="grid grid-cols-2 gap-6">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-2">Earnings</p>
              <table className="w-full"><tbody>
                {erow('Basic Salary', ps.basic)}
                {erow('HRA', ps.hra)}
                {Number(ps.da)>0 && erow('Dearness Allowance', ps.da)}
                {Number(ps.conveyance)>0 && erow('Conveyance', ps.conveyance)}
                {Number(ps.medical_allowance)>0 && erow('Medical Allowance', ps.medical_allowance)}
                {Number(ps.lta)>0 && erow('LTA', ps.lta)}
                {Number(ps.special_allowance)>0 && erow('Special Allowance', ps.special_allowance)}
                {Number(ps.lop_deduction)>0 && <tr className="border-b border-gray-100"><td className="py-1.5 pr-4 text-[12px] text-red-500">LOP Deduction</td><td className="py-1.5 text-right text-[12px] text-red-500">−{formatCurrency(ps.lop_deduction)}</td></tr>}
                {erow('Gross Salary', ps.gross_salary, true)}
              </tbody></table>
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-2">Deductions</p>
              <table className="w-full"><tbody>
                {Number(ps.pf_employee)>0 && drow('PF Employee (12%)', ps.pf_employee)}
                {Number(ps.pf_employer)>0 && <tr className="border-b border-gray-100"><td className="py-1.5 pr-4 text-[12px] text-gray-400">PF Employer (12%) *</td><td className="py-1.5 text-right text-[12px] text-gray-400">{formatCurrency(ps.pf_employer)}</td></tr>}
                {Number(ps.esic_employee)>0 && drow('ESIC Employee (0.75%)', ps.esic_employee)}
                {Number(ps.pt)>0 && drow('Professional Tax', ps.pt)}
                {Number(ps.tds)>0 && drow('TDS / Income Tax', ps.tds)}
                {Number(ps.other_deductions)>0 && drow('Other Deductions', ps.other_deductions)}
                {drow('Total Deductions', totalDed, true)}
              </tbody></table>
              {Number(ps.pf_employer)>0 && <p className="text-[10px] text-gray-400 mt-1">* Employer PF not deducted from salary.</p>}
            </div>
          </div>

          {/* Net Pay */}
          <div className="flex items-center justify-between p-4 bg-gray-900 rounded-[12px] text-white">
            <div>
              <p className="text-[11px] text-gray-400 uppercase tracking-widest">Net Take-Home</p>
              <p className="text-[11px] text-gray-400">{payPeriod}</p>
            </div>
            <p className="text-[28px] font-black">{formatCurrency(ps.net_pay)}</p>
          </div>

          {ps.company_bank_name && (
            <p className="text-[10px] text-gray-400 text-center">Credited from {ps.company_account_holder} — {ps.company_bank_name} ({ps.company_ifsc})</p>
          )}
          <p className="text-[10px] text-gray-400 text-center pt-2">Computer-generated payslip — no signature required.</p>
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function PayrollRunDetailPage() {
  const { runId } = useParams();
  const navigate = useNavigate();
  const { data, isLoading } = usePayrollRun(runId);
  const markPaid = useMarkPayrollPaid();
  const [payslipItem, setPayslipItem] = useState(null);

  const run = data?.data;
  const items = run?.items || [];

  if (isLoading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-[var(--accent)] border-t-transparent rounded-full animate-spin"/></div>;
  if (!run) return <div className="text-center py-16 text-[var(--text-tertiary)]">Payroll run not found</div>;

  const handleMarkPaid = async () => {
    if (!confirm('Confirm salary has been disbursed and mark this run as PAID?')) return;
    try { await markPaid.mutateAsync(runId); toast.success('Marked as Paid'); } catch { toast.error('Failed'); }
  };

  return (
    <div className="space-y-5 fade-in">
      <PageHeader
        title={run.pay_period}
        subtitle={`${run.total_employees} employees · ${run.period_start} to ${run.period_end}`}
        breadcrumb={[{ label: 'Payroll', href: '/payroll' }, { label: run.pay_period }]}
        actions={
          <div className="flex items-center gap-2">
            <StatusBadge status={run.status}/>
            {run.status === 'PROCESSED' && (
              <Button size="sm" icon={CheckCircle} onClick={handleMarkPaid} loading={markPaid.isPending}>Mark as Paid</Button>
            )}
          </div>
        }
      />

      {/* Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {[
          ['Employees', run.total_employees, 'text-[var(--text-primary)]'],
          ['Total Gross', formatCurrency(run.total_gross), 'text-[var(--text-primary)]'],
          ['PF (Emp)', formatCurrency(run.total_pf_employee), 'text-red-500'],
          ['ESIC+PT', formatCurrency(Number(run.total_esic_employee)+Number(run.total_pt)), 'text-red-500'],
          ['Net Payable', formatCurrency(run.total_net), 'text-green-600'],
        ].map(([l,v,cls]) => (
          <div key={l} className="card p-4">
            <p className="text-[10px] text-[var(--text-tertiary)] uppercase tracking-wide">{l}</p>
            <p className={`text-[17px] font-black mt-0.5 ${cls}`}>{v}</p>
          </div>
        ))}
      </div>

      {/* Statutory */}
      <div className="card p-4">
        <p className="text-[11px] font-bold uppercase tracking-widest text-[var(--text-tertiary)] mb-3">Statutory Challans (Company's Contribution)</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-[13px]">
          <div><p className="text-[var(--text-secondary)] text-[12px]">PF Employer Share</p><p className="font-bold">{formatCurrency(run.total_pf_employer)}</p></div>
          <div><p className="text-[var(--text-secondary)] text-[12px]">ESIC Employer Share</p><p className="font-bold">{formatCurrency(run.total_esic_employer)}</p></div>
          <div><p className="text-[var(--text-secondary)] text-[12px]">Total PF Challan</p><p className="font-bold text-[var(--accent)]">{formatCurrency(Number(run.total_pf_employee)+Number(run.total_pf_employer))}</p></div>
          <div><p className="text-[var(--text-secondary)] text-[12px]">Total ESIC Challan</p><p className="font-bold text-[var(--accent)]">{formatCurrency(Number(run.total_esic_employee)+Number(run.total_esic_employer))}</p></div>
        </div>
      </div>

      {/* Items */}
      <div className="card p-0 overflow-x-auto">
        <div className="px-5 py-3 border-b border-[var(--border)] flex justify-between items-center">
          <p className="text-[14px] font-semibold text-[var(--text-primary)]">Employee Payslips</p>
          <span className="text-[12px] text-[var(--text-tertiary)]">{items.length} employees</span>
        </div>
        <table className="w-full">
          <thead>
            <tr className="border-b border-[var(--border)]">
              {['Employee','Days Present','LOP','Gross','Deductions','Net Pay','Status','Payslip'].map(h=>(
                <th key={h} className="text-left text-[11px] font-bold uppercase tracking-wide text-[var(--text-tertiary)] px-4 py-3">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-[var(--border)]">
            {items.map(item => (
              <tr key={item.id} className="hover:bg-[var(--bg-muted)] transition-colors">
                <td className="px-4 py-3.5">
                  <div className="flex items-center gap-2.5">
                    <Avatar name={item.employee_name} size="sm"/>
                    <div>
                      <p className="text-[13px] font-semibold text-[var(--text-primary)]">{item.employee_name}</p>
                      <p className="text-[10px] text-[var(--text-tertiary)]">{item.employee_code} · {item.department}</p>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3.5 text-[13px] font-semibold text-green-600">{item.present_days}/{item.total_working_days}</td>
                <td className="px-4 py-3.5 text-[13px] text-red-500">{Number(item.lop_days)>0 ? item.lop_days : '—'}</td>
                <td className="px-4 py-3.5 text-[13px]">{formatCurrency(item.gross_salary)}</td>
                <td className="px-4 py-3.5 text-[13px] text-red-500">
                  −{formatCurrency(Number(item.pf_employee)+Number(item.esic_employee)+Number(item.pt)+Number(item.tds))}
                </td>
                <td className="px-4 py-3.5 text-[13px] font-bold text-green-600">{formatCurrency(item.net_pay)}</td>
                <td className="px-4 py-3.5">
                  <span className={`text-[11px] px-2 py-0.5 rounded-full font-semibold ${item.payment_status==='PAID'?'bg-green-100 text-green-700':'bg-amber-100 text-amber-700'}`}>
                    {item.payment_status}
                  </span>
                </td>
                <td className="px-4 py-3.5">
                  <button onClick={() => setPayslipItem(item)} className="flex items-center gap-1 text-[11px] font-semibold text-[var(--accent)] hover:underline">
                    <Eye size={12}/> View
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {payslipItem && <PayslipModal runId={runId} item={{...payslipItem, pay_period: run.pay_period, period_start: run.period_start, period_end: run.period_end}} onClose={() => setPayslipItem(null)}/>}
    </div>
  );
}
